import { z } from "zod";

// ---------------------------------------------------------------------------
// Scheduled test invitations, reminders and readiness (GRA-173)
//
// Timing constants, the invite batch document, the board status a session
// shows as, and team readiness. Pure; shared by business-api, the Trigger.dev
// tasks and the webapp.
// ---------------------------------------------------------------------------

/** The personal link is valid this long after the email goes out. */
export const INVITE_VALIDITY_DAYS = 14;
/** Reminders to those who have not finished, counted from the send. */
export const INVITE_REMINDER_DAYS = [3, 7] as const;
export type InviteReminderDay = (typeof INVITE_REMINDER_DAYS)[number];
/** How far ahead a manager may schedule (decision 2026-10-23: Capacio invitations stay young). */
export const INVITE_MAX_SCHEDULE_DAYS = 14;
/** A scheduled send must be at least this far in the future. */
export const INVITE_MIN_SCHEDULE_MINUTES = 2;
/** The signed return state outlives the link by a grace period for late completions. */
export const INVITE_RETURN_STATE_GRACE_DAYS = 7;

export const DAY_MS = 24 * 60 * 60 * 1000;

export function addDays(from: Date, days: number): Date {
  return new Date(from.getTime() + days * DAY_MS);
}

/** Validates a requested send time against now; returns the reason it is refused, or null. */
export function scheduledAtProblem(
  scheduledAt: Date,
  now: Date = new Date(),
): "past" | "tooFar" | null {
  if (scheduledAt.getTime() < now.getTime() + INVITE_MIN_SCHEDULE_MINUTES * 60 * 1000) return "past";
  if (scheduledAt.getTime() > addDays(now, INVITE_MAX_SCHEDULE_DAYS).getTime()) return "tooFar";
  return null;
}

// ── What the Trigger tasks need, stored on the session ─────────────────────

export const AssessmentInviteContentSchema = z.object({
  cognitiveUrl: z.string().min(1),
  personalityUrl: z.string().nullable(),
  greeting: z.string().nullable(),
  managerName: z.string().nullable(),
  unitName: z.string().nullable(),
});
export type AssessmentInviteContent = z.infer<typeof AssessmentInviteContentSchema>;

export const AssessmentInviteRemindersSchema = z.object({
  enabled: z.boolean(),
  /** Reminder days already sent (3, 7). */
  sentDays: z.array(z.number().int()),
});
export type AssessmentInviteReminders = z.infer<typeof AssessmentInviteRemindersSchema>;

/** What the provider reports about progress, when it reports anything. */
export const ASSESSMENT_PROVIDER_STATUSES = ["invited", "opened", "started", "completed"] as const;
export type AssessmentProviderStatus = (typeof ASSESSMENT_PROVIDER_STATUSES)[number];

// ── Invite batch ───────────────────────────────────────────────────────────

export const AssessmentInviteBatchSchema = z.object({
  id: z.string().min(1),
  businessId: z.string().min(1),
  createdBy: z.string().min(1),
  createdAt: z.string().nullable(),
  mode: z.enum(["now", "scheduled"]),
  scheduledFor: z.string().nullable(),
  sentAt: z.string().nullable(),
  remindersEnabled: z.boolean(),
  providerId: z.string().min(1),
  sessions: z.array(z.object({ memberId: z.string().min(1), sessionId: z.string().min(1) })),
  triggerRuns: z.object({
    send: z.string().optional(),
    reminder3: z.string().optional(),
    reminder7: z.string().optional(),
    expire: z.string().optional(),
  }),
  triggerError: z.string().nullable(),
  counts: z.object({ total: z.number().int(), sent: z.number().int(), failed: z.number().int() }),
});
export type AssessmentInviteBatch = z.infer<typeof AssessmentInviteBatchSchema>;

// ── Board status ───────────────────────────────────────────────────────────

export const INVITE_BOARD_STATUSES = [
  "scheduled",
  "invited",
  "opened",
  "started",
  "done",
  "expired",
  "cancelled",
  "error",
] as const;
export type InviteBoardStatus = (typeof INVITE_BOARD_STATUSES)[number];

export interface InviteBoardInput {
  status: "pending" | "scheduled" | "completed" | "expired" | "cancelled";
  inviteError: string | null;
  expiresAt: string | null;
  providerStatus: AssessmentProviderStatus | null;
}

/** What the status board shows for one session. */
export function deriveInviteBoardStatus(
  session: InviteBoardInput,
  now: Date = new Date(),
): InviteBoardStatus {
  if (session.status === "cancelled") return "cancelled";
  if (session.status === "completed") return "done";
  if (session.inviteError) return "error";
  if (session.status === "expired") return "expired";
  if (session.status === "scheduled") return "scheduled";
  if (session.expiresAt && new Date(session.expiresAt).getTime() <= now.getTime()) return "expired";
  if (session.providerStatus === "started") return "started";
  if (session.providerStatus === "opened") return "opened";
  return "invited";
}

export const TERMINAL_BOARD_STATUSES: readonly InviteBoardStatus[] = ["done", "expired", "cancelled", "error"];

// ── Team readiness ─────────────────────────────────────────────────────────

/** A team profile is ready at 3 or more done and 70 % of the team (decision 2026-10-23). */
export const TEAM_READINESS = { minDone: 3, minShare: 0.7 } as const;

export function isTeamReady(done: number, total: number): boolean {
  if (total <= 0) return false;
  return done >= TEAM_READINESS.minDone && done / total >= TEAM_READINESS.minShare;
}

export interface TeamReadiness {
  salesTeamId: string;
  name: string;
  total: number;
  invited: number;
  done: number;
  ready: boolean;
}

/**
 * Readiness per team from the roster and the invite sessions: `done` counts
 * members with a completed provider session, `invited` those with any
 * live or completed one.
 */
export function computeTeamReadiness(input: {
  teams: Array<{ id: string; name: string }>;
  members: Array<{ id: string; salesTeamId: string | null }>;
  sessions: Array<{ memberId: string; status: InviteBoardInput["status"] }>;
}): TeamReadiness[] {
  const doneMembers = new Set<string>();
  const invitedMembers = new Set<string>();
  for (const session of input.sessions) {
    if (session.status === "completed") doneMembers.add(session.memberId);
    if (session.status === "completed" || session.status === "pending" || session.status === "scheduled") {
      invitedMembers.add(session.memberId);
    }
  }
  return input.teams.map((team) => {
    const members = input.members.filter((m) => m.salesTeamId === team.id);
    const done = members.filter((m) => doneMembers.has(m.id)).length;
    const invited = members.filter((m) => invitedMembers.has(m.id)).length;
    return {
      salesTeamId: team.id,
      name: team.name,
      total: members.length,
      invited,
      done,
      ready: isTeamReady(done, members.length),
    };
  });
}
